import React from 'react';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import useCareTeamMembers from '../../../../../hooks/api/useCareTeamMembers';
import { usePromptButton } from '../../../../shared/PromptButton';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('deleteCareTeamMemberButton', {
    cancel: 'Cancel',
    deletePrompt: 'Delete Care Team Member?',
    deleteNotificationHeader: 'Are you sure you want to delete this care team member?',
    deleteSubtext:
        'This will permanently delete this care team member from your profile. You can always add them back later.',
    deleteButton: 'Yes, delete',
    cancelButton: 'No, cancel',
});

export type UseDeleteCareTeamMemberButtonArgs = {
    medicalProfileId: string;
    careTeamMemberId: string;
};

export type UseDeleteCareTeamMemberButtonReturnValue = {
    onPress: () => void;
    Prompt: React.ReactNode;
    isDeletingCareTeamMember: boolean;
};

export const useDeleteCareTeamMemberButton = (
    props: UseDeleteCareTeamMemberButtonArgs,
): UseDeleteCareTeamMemberButtonReturnValue => {
    const goBack = useGoBack();
    const { deleteCareTeamMember, isDeletingCareTeamMember } = useCareTeamMembers({
        medicalProfileId: props.medicalProfileId,
        onDeleteCareTeamMemberSuccess: () => {
            goBack();
        },
    });

    const deleteCareTeamMemberButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <>
                <Text variant="body-1">{intl('deleteNotificationHeader')}</Text>
                <Text variant="body-1">{intl('deleteSubtext')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                deleteCareTeamMember(props.careTeamMemberId);
                                onClose();
                            },
                            variant: 'alert',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                        },
                    ]}
                />
            </>
        ),
        promptTitle: intl('deletePrompt'),
    });

    return {
        onPress: deleteCareTeamMemberButton.onPress,
        Prompt: deleteCareTeamMemberButton.Prompt,
        isDeletingCareTeamMember,
    };
};
